import { useEffect, useRef, useCallback } from 'react';
import {
  Viewer,
  Entity,
  BillboardGraphics,
  PointGraphics,
  PolylineGraphics,
  PolygonGraphics,
  LabelGraphics,
  CameraFlyTo,
} from 'resium';
import {
  Cartesian3,
  Cartesian2,
  Color,
  Viewer as CesiumViewer,
  ScreenSpaceEventType,
  defined,
  Math as CesiumMath,
  HeightReference,
  VerticalOrigin,
  HorizontalOrigin,
  NearFarScalar,
  LabelStyle,
  Cartographic,
  createWorldTerrainAsync,
  PolylineGlowMaterialProperty,
  PolylineDashMaterialProperty,
} from 'cesium';
import { useMapStore } from '../../store/mapStore';
import { applyBasemapToViewer } from './basemaps';
import { useFAAFacilityMap } from '../../hooks/useFAAFacilityMap';
import { useDronetagCloudLayer } from '../../hooks/useDronetagCloudLayer';
import { useFlightReplay } from '../../hooks/useFlightReplay';
import { useOpenSkyLayer, getAircraftByHex } from '../../hooks/useOpenSkyLayer';
import { airlineFromCallsign } from '../../services/airlineLookup';
import { useAdsbReplayLayer } from '../../hooks/useAdsbReplayLayer';
import { getSensorByEntityId, useSensorLayer } from '../../hooks/useSensorLayer';
import { useAirspaceLayer } from '../../hooks/useAirspaceLayer';
import { TrackClassification } from '@airrecon/shared';
import { DRONE_ICON_DATA_URI, PILOT_ICON_DATA_URI } from './icons';

// Track color mapping
const TRACK_COLORS: Record<TrackClassification, Color> = {
  [TrackClassification.UNKNOWN]: Color.GRAY,
  [TrackClassification.DRONE_SMALL]: Color.LIME,
  [TrackClassification.DRONE_MEDIUM]: Color.YELLOW,
  [TrackClassification.DRONE_LARGE]: Color.ORANGE,
  [TrackClassification.AIRCRAFT_GENERAL]: Color.CYAN,
  [TrackClassification.AIRCRAFT_COMMERCIAL]: Color.BLUE,
  [TrackClassification.HELICOPTER]: Color.fromCssColorString('#0088ff'),
  [TrackClassification.BIRD]: Color.GRAY.withAlpha(0.5),
  [TrackClassification.WEATHER]: Color.WHITE.withAlpha(0.3),
  [TrackClassification.CLUTTER]: Color.DARKGRAY.withAlpha(0.3),
};

function pickedEntityId(pickedObject: any): string | null {
  const id = pickedObject?.id;
  if (typeof id === 'string') return id;
  if (typeof id?.id === 'string') return id.id;
  const primitiveId = pickedObject?.primitive?.id;
  if (typeof primitiveId === 'string') return primitiveId;
  if (typeof primitiveId?.id === 'string') return primitiveId.id;
  return null;
}

export default function CesiumMap() {
  const viewerRef = useRef<CesiumViewer | null>(null);

  const {
    layers,
    liveTracks,
    liveTrackTrails,
    selectedTrackId,
    setSelectedTrack,
    geofences,
    selectedGeofenceId,
    setSelectedGeofence,
    missions,
    selectedMissionId,
    setSelectedMission,
    setSelectedAircraft,
    setSelectedSensor,
    flyToTarget,
    drawingMode,
    addDrawnCoordinate,
    dronetagApiToken,
    viewer: storeViewer,
    basemapId,
  } = useMapStore();

  // FAA Facility Map layer (3D extruded polygons)
  useFAAFacilityMap(storeViewer, layers.facilities);

  // Dronetag Cloud layer (live drone positions)
  useDronetagCloudLayer(storeViewer, layers.dronetagCloud, dronetagApiToken);

  // Flight replay layer (historical track animation)
  const replayTrackId = useMapStore((s) => s.replayTrackId);
  useFlightReplay(storeViewer, replayTrackId, !!replayTrackId);

  // OpenSky ADS-B layer (live air traffic)
  useOpenSkyLayer(storeViewer, layers.openSky);

  // Sensor coverage markers (status-colored pins for deployed sensors)
  useSensorLayer(storeViewer, layers.sensors);

  // Airspace overlay — TFRs, SUA, Prohibited/Restricted/MOA/class polygons.
  // Phase 4 item 20; populated by the FAA TFR cron in the API service.
  useAirspaceLayer(storeViewer, layers.airspace);

  // ADS-B replay layer (historical air traffic during flight replay)
  useAdsbReplayLayer(storeViewer, replayTrackId, !!replayTrackId);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      viewerRef.current = null;
      useMapStore.getState().setViewer(null);
    };
  }, []);

  // Swap base imagery when the user changes the basemap selection.
  // The ref callback handles the initial load, so this covers every
  // subsequent selection change.
  useEffect(() => {
    if (!storeViewer) return;
    void applyBasemapToViewer(storeViewer, basemapId);
  }, [storeViewer, basemapId]);

  // Handle click events for selection and drawing
  const handleClick = useCallback(
    (movement: any) => {
      if (!viewerRef.current) return;

      const viewer = viewerRef.current;

      // Drawing mode
      if (drawingMode !== 'NONE') {
        const cartesian = viewer.scene.pickPosition(movement.position);
        if (defined(cartesian)) {
          const cartographic = Cartographic.fromCartesian(cartesian);
          addDrawnCoordinate([
            CesiumMath.toDegrees(cartographic.longitude),
            CesiumMath.toDegrees(cartographic.latitude),
          ]);
        }
        return;
      }

      // Selection mode
      const pickedObject = viewer.scene.pick(movement.position);
      if (defined(pickedObject)) {
        const entityId = pickedEntityId(pickedObject);
        if (!entityId) {
          setSelectedSensor(null);
          return;
        }

        if (entityId.startsWith('track-')) {
          setSelectedTrack(entityId.replace('track-', ''));
          setSelectedAircraft(null);
          setSelectedSensor(null);
        } else if (entityId.startsWith('geofence-')) {
          setSelectedGeofence(entityId.replace('geofence-', ''));
          setSelectedAircraft(null);
          setSelectedSensor(null);
        } else if (entityId.startsWith('mission-')) {
          setSelectedMission(entityId.replace('mission-', ''));
          setSelectedAircraft(null);
          setSelectedSensor(null);
        } else if (entityId.startsWith('aircraft-')) {
          // ADS-B click → look up the aircraft from the hook's snapshot
          // map and store a typed snapshot in the store. The popup reads
          // it from there. Bail silently if the snapshot has gone stale.
          const hex = entityId.replace('aircraft-', '');
          const ac = getAircraftByHex(hex);
          if (ac) {
            setSelectedAircraft({
              hex: ac.hex,
              flight: ac.flight,
              registration: ac.registration,
              aircraftType: ac.aircraftType,
              latitude: ac.latitude,
              longitude: ac.longitude,
              altitudeBaro: ac.altitudeBaro,
              altitudeGeom: ac.altitudeGeom,
              groundSpeed: ac.groundSpeed,
              track: ac.track,
              baroRate: ac.baroRate,
              squawk: ac.squawk,
              category: ac.category,
              onGround: ac.onGround,
              airline: airlineFromCallsign(ac.flight),
              capturedAt: Date.now(),
            });
            setSelectedTrack(null);
            setSelectedSensor(null);
          }
        } else if (entityId.startsWith('sensor-')) {
          const sensor = getSensorByEntityId(entityId);
          if (sensor) {
            setSelectedSensor({
              ...sensor,
            });
            setSelectedTrack(null);
            setSelectedAircraft(null);
          }
        } else {
          setSelectedSensor(null);
        }
      } else {
        setSelectedTrack(null);
        setSelectedGeofence(null);
        setSelectedMission(null);
        setSelectedAircraft(null);
        setSelectedSensor(null);
      }
    },
    [
      drawingMode,
      addDrawnCoordinate,
      setSelectedTrack,
      setSelectedGeofence,
      setSelectedMission,
      setSelectedAircraft,
      setSelectedSensor,
    ]
  );

  const handleMouseMove = useCallback((movement: any) => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    const pickedObject = viewer.scene.pick(movement.endPosition);
    const entityId = defined(pickedObject) ? pickedEntityId(pickedObject) : null;
    const isSensor = !!entityId && !!getSensorByEntityId(entityId);
    viewer.scene.canvas.style.cursor = isSensor ? 'pointer' : '';
  }, []);

  // Set up event handlers
  useEffect(() => {
    if (!viewerRef.current) return;

    const viewer = viewerRef.current;
    const handler = viewer.screenSpaceEventHandler;

    handler.setInputAction(handleClick, ScreenSpaceEventType.LEFT_CLICK);
    handler.setInputAction(handleMouseMove, ScreenSpaceEventType.MOUSE_MOVE);

    return () => {
      handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
      handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      viewer.scene.canvas.style.cursor = '';
    };
  }, [handleClick, handleMouseMove]);

  return (
    <Viewer
      ref={(ref) => {
        if (ref?.cesiumElement && ref.cesiumElement !== viewerRef.current) {
          const viewer = ref.cesiumElement;
          viewerRef.current = viewer;
          console.log('[CesiumMap] Viewer captured via ref callback');

          // Set up imagery — delegate to the shared basemap module so the
          // initial layer matches the user's persisted selection (and can be
          // swapped live from the Map page picker).
          void applyBasemapToViewer(viewer, useMapStore.getState().basemapId);

          // Set up terrain
          createWorldTerrainAsync().then((terrain) => {
            if (!viewer.isDestroyed()) {
              viewer.terrainProvider = terrain;
            }
          });

          // Expose to store so FAA layer hook + nav buttons can access it
          useMapStore.getState().setViewer(viewer);
          console.log('[CesiumMap] Store viewer set');
        }
      }}
      style={{ width: '100%', height: '100%' }}
      timeline={false}
      animation={false}
      baseLayerPicker={false}
      geocoder={false}
      homeButton={false}
      sceneModePicker={false}
      navigationHelpButton={false}
      selectionIndicator={false}
      infoBox={false}
      // Scene mode is controlled imperatively by MapPage via morphTo2D /
      // morphTo3D so the user's 2D/3D segmented toggle actually works.
      // Setting scene3DOnly={true} (the prior value) silently blocked the
      // morph and broke the FAA Facilities Map in 2D view.
    >
      {/* Fly to target */}
      {flyToTarget && (
        <CameraFlyTo
          destination={Cartesian3.fromDegrees(
            flyToTarget.longitude,
            flyToTarget.latitude,
            flyToTarget.height
          )}
          duration={1.5}
        />
      )}

      {/* ============================================================
          LIVE TRACK FLIGHT PATH TRAILS
          Rendered BEFORE the drone icon so the trail sits beneath it.
          ============================================================ */}
      {layers.liveTracks &&
        Array.from(liveTrackTrails.entries())
          .filter(([, trail]) => trail.length >= 2)
          .map(([trackId, trail]) => {
            const track = liveTracks.get(trackId);
            const trailColor = track
              ? TRACK_COLORS[track.classification].withAlpha(0.85)
              : Color.LIME.withAlpha(0.85);
            return (
              <Entity key={`trail-${trackId}`} id={`trail-${trackId}`}>
                <PolylineGraphics
                  positions={trail.map((p) =>
                    Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitudeMSL)
                  )}
                  width={4}
                  material={new PolylineGlowMaterialProperty({
                    glowPower: 0.15,
                    color: trailColor,
                  })}
                  clampToGround={false}
                />
              </Entity>
            );
          })}

      {/* ============================================================
          LIVE TRACK DRONE ICONS
          Details open via DOM click targets in SensorTooltip.
          ============================================================ */}
      {layers.liveTracks &&
        Array.from(liveTracks.values())
          // Dedrone emits the controller signal as a separate track row with
          // detectionType='remote'. Those are rendered below as pilot icons,
          // not as drone icons. Anything else (including Dronetag rows, which
          // never set detectionType) renders as a drone here.
          .filter((track) => {
            const meta = track.metadata as Record<string, unknown> | undefined;
            return (meta?.detectionType as string | undefined) !== 'remote';
          })
          .map((track) => {
          const isSelected = selectedTrackId === track.trackId;

          return (
            <Entity
              key={`track-${track.trackId}`}
              id={`track-${track.trackId}`}
              position={Cartesian3.fromDegrees(
                track.longitude,
                track.latitude,
                track.altitudeMSL,
              )}
              selected={isSelected}
            >
              {/* Drone icon billboard. disableDepthTestDistance=∞ keeps
                  the icon visible even when terrain is in front of it
                  from the camera's angle — without this the drone winks
                  out behind nearby hills. */}
              <BillboardGraphics
                image={DRONE_ICON_DATA_URI}
                width={isSelected ? 66 : 54}
                height={isSelected ? 66 : 54}
                heightReference={HeightReference.NONE}
                scaleByDistance={new NearFarScalar(500, 1.0, 80000, 0.4)}
                rotation={track.heading ? -CesiumMath.toRadians(track.heading) : 0}
                alignedAxis={Cartesian3.UNIT_Z}
                disableDepthTestDistance={Number.POSITIVE_INFINITY}
              />
            </Entity>
          );
        })}

      {/* ============================================================
          UNPAIRED DEDRONE CONTROLLERS (standalone pilot icons)
          When the Dedrone ingestion couldn't pair a 'remote' to a
          recent home detection (no matching drone within the pairing
          window), the row is written standalone. Render it as a pilot
          icon at the controller's own position so the operator can
          still see where to physically intercept the pilot — the most
          actionable intel for stopping an unauthorized flight.
          Paired remotes are skipped at write time and their coords
          ride along on the home track's operator-marker block below.
          ============================================================ */}
      {layers.liveTracks &&
        Array.from(liveTracks.values())
          .filter((track) => {
            const meta = track.metadata as Record<string, unknown> | undefined;
            return (meta?.detectionType as string | undefined) === 'remote';
          })
          .map((track) => (
            <Entity
              key={`remote-${track.trackId}`}
              id={`remote-${track.trackId}`}
              position={Cartesian3.fromDegrees(
                track.longitude,
                track.latitude,
                0,
              )}
            >
              <BillboardGraphics
                image={PILOT_ICON_DATA_URI}
                width={45}
                height={45}
                heightReference={HeightReference.CLAMP_TO_GROUND}
                verticalOrigin={VerticalOrigin.BOTTOM}
                scaleByDistance={new NearFarScalar(500, 1.0, 80000, 0.4)}
                disableDepthTestDistance={Number.POSITIVE_INFINITY}
              />
            </Entity>
          ))}

      {/* ============================================================
          OPERATOR (PILOT) MARKERS
          Person icon for live tracks that have
          operator position in metadata.
          ============================================================ */}
      {layers.liveTracks &&
        Array.from(liveTracks.values())
          .filter((track) => {
            const meta = track.metadata as Record<string, unknown> | undefined;
            return (
              meta &&
              typeof meta.operatorLatitude === 'number' &&
              typeof meta.operatorLongitude === 'number' &&
              !(meta.operatorLatitude === 0 && meta.operatorLongitude === 0)
            );
          })
          .map((track) => {
            const meta = track.metadata as Record<string, unknown>;
            const isSelected = selectedTrackId === track.trackId;
            return (
              <Entity
                key={`operator-${track.trackId}`}
                id={`operator-${track.trackId}`}
                position={Cartesian3.fromDegrees(
                  meta.operatorLongitude as number,
                  meta.operatorLatitude as number,
                  (meta.operatorAltitudeMSL as number) ?? 0,
                )}
              >
                {/* Pilot billboard — anchor the BOTTOM of the SVG to the
                    terrain. Default verticalOrigin is CENTER, which with
                    CLAMP_TO_GROUND puts the middle of the person sprite
                    at ground level so the body/legs render underground.
                    disableDepthTestDistance=∞ is the OTHER reason the
                    pilot was sometimes invisible — without it, terrain in
                    front of the operator's position (a hill between camera
                    and pilot) occludes the sprite and the icon vanishes.
                    Always-visible billboards trade depth-correctness for
                    operator-readability, which is the right call here. */}
                <BillboardGraphics
                  image={PILOT_ICON_DATA_URI}
                  width={isSelected ? 57 : 45}
                  height={isSelected ? 57 : 45}
                  heightReference={HeightReference.CLAMP_TO_GROUND}
                  verticalOrigin={VerticalOrigin.BOTTOM}
                  scaleByDistance={new NearFarScalar(500, 1.0, 80000, 0.4)}
                  disableDepthTestDistance={Number.POSITIVE_INFINITY}
                />
              </Entity>
            );
          })}

      {/* ============================================================
          DRONE-TO-PILOT CONNECTING LINES
          Thin amber dashed line linking drone position to pilot on ground.
          ============================================================ */}
      {layers.liveTracks &&
        Array.from(liveTracks.values())
          .filter((track) => {
            const meta = track.metadata as Record<string, unknown> | undefined;
            return (
              meta &&
              typeof meta.operatorLatitude === 'number' &&
              typeof meta.operatorLongitude === 'number' &&
              !(meta.operatorLatitude === 0 && meta.operatorLongitude === 0)
            );
          })
          .map((track) => {
            const meta = track.metadata as Record<string, unknown>;
            return (
              <Entity key={`op-link-${track.trackId}`} id={`op-link-${track.trackId}`}>
                <PolylineGraphics
                  positions={[
                    Cartesian3.fromDegrees(track.longitude, track.latitude, track.altitudeMSL),
                    Cartesian3.fromDegrees(
                      meta.operatorLongitude as number,
                      meta.operatorLatitude as number,
                      0,
                    ),
                  ]}
                  width={2}
                  material={new PolylineDashMaterialProperty({
                    color: Color.fromCssColorString('#f59e0b').withAlpha(0.5),
                    dashLength: 12,
                    dashPattern: 255,
                  })}
                  clampToGround={false}
                />
              </Entity>
            );
          })}

      {/* Geofences */}
      {layers.geofences &&
        geofences.map((geofence) => {
          const isSelected = selectedGeofenceId === geofence.id;
          const color =
            geofence.type === 'EXCLUSION'
              ? Color.RED
              : geofence.type === 'INCLUSION'
              ? Color.GREEN
              : Color.YELLOW;

          // Handle polygon geometry
          if (geofence.geometry?.type === 'Polygon') {
            const coordinates = (geofence.geometry as any).coordinates[0];
            const positions = coordinates.map(([lng, lat]: [number, number]) =>
              Cartesian3.fromDegrees(lng, lat, geofence.altitudeMax || 200)
            );

            return (
              <Entity
                key={`geofence-${geofence.id}`}
                id={`geofence-${geofence.id}`}
                name={geofence.name}
              >
                <PolygonGraphics
                  hierarchy={positions}
                  material={color.withAlpha(isSelected ? 0.4 : 0.2)}
                  outline
                  outlineColor={color}
                  outlineWidth={isSelected ? 3 : 2}
                  height={geofence.altitudeMin || 0}
                  extrudedHeight={geofence.altitudeMax || 200}
                />
              </Entity>
            );
          }

          // Handle circle geometry
          if (geofence.geometry?.type === 'Circle') {
            const [lng, lat] = (geofence.geometry as any).center;
            const radius = (geofence.geometry as any).radiusMeters;

            // Create circle polygon (approximation)
            const positions: Cartesian3[] = [];
            for (let i = 0; i <= 64; i++) {
              const angle = (i / 64) * 2 * Math.PI;
              const dx = radius * Math.cos(angle);
              const dy = radius * Math.sin(angle);
              // Approximate offset in degrees
              const dLng = dx / (111320 * Math.cos((lat * Math.PI) / 180));
              const dLat = dy / 111320;
              positions.push(
                Cartesian3.fromDegrees(lng + dLng, lat + dLat, geofence.altitudeMax || 200)
              );
            }

            return (
              <Entity
                key={`geofence-${geofence.id}`}
                id={`geofence-${geofence.id}`}
                name={geofence.name}
              >
                <PolygonGraphics
                  hierarchy={positions}
                  material={color.withAlpha(isSelected ? 0.4 : 0.2)}
                  outline
                  outlineColor={color}
                  outlineWidth={isSelected ? 3 : 2}
                  height={geofence.altitudeMin || 0}
                  extrudedHeight={geofence.altitudeMax || 200}
                />
              </Entity>
            );
          }

          return null;
        })}

      {/* Missions */}
      {layers.missions &&
        missions.map((mission) => {
          const isSelected = selectedMissionId === mission.id;
          const waypoints = (mission.route as any)?.waypoints || [];

          if (waypoints.length < 2) return null;

          const positions = waypoints.map((wp: any) =>
            Cartesian3.fromDegrees(wp.longitude, wp.latitude, wp.altitudeMSL)
          );

          return (
            <Entity
              key={`mission-${mission.id}`}
              id={`mission-${mission.id}`}
              name={mission.name}
            >
              {/* Route line */}
              <PolylineGraphics
                positions={positions}
                width={isSelected ? 4 : 2}
                material={Color.ORANGE}
              />

              {/* Waypoints */}
              {waypoints.map((wp: any, index: number) => (
                <Entity
                  key={`mission-${mission.id}-wp-${index}`}
                  position={Cartesian3.fromDegrees(wp.longitude, wp.latitude, wp.altitudeMSL)}
                >
                  <PointGraphics
                    pixelSize={isSelected ? 10 : 6}
                    color={index === 0 ? Color.GREEN : index === waypoints.length - 1 ? Color.RED : Color.WHITE}
                    outlineColor={Color.ORANGE}
                    outlineWidth={2}
                  />
                  <LabelGraphics
                    text={wp.name || `WP${index + 1}`}
                    font="600 11px 'JetBrains Mono', monospace"
                    fillColor={Color.WHITE}
                    style={LabelStyle.FILL}
                    showBackground
                    backgroundColor={Color.fromCssColorString('rgba(11, 13, 16, 0.78)')}
                    backgroundPadding={new Cartesian2(6, 3)}
                    verticalOrigin={VerticalOrigin.BOTTOM}
                    horizontalOrigin={HorizontalOrigin.CENTER}
                    pixelOffset={new Cartesian2(0, -10)}
                    scaleByDistance={new NearFarScalar(500, 1, 20000, 0.5)}
                  />
                </Entity>
              ))}
            </Entity>
          );
        })}
    </Viewer>
  );
}
